import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowRight, AlertTriangle, ShieldAlert, FileWarning, Calculator } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ICON_BY_CATEGORY = {
  leve: { icon: AlertTriangle, color: "text-amber-600", bg: "bg-amber-50", border: "border-amber-200" },
  grave: { icon: FileWarning, color: "text-orange-600", bg: "bg-orange-50", border: "border-orange-200" },
  gravisima: { icon: ShieldAlert, color: "text-red-600", bg: "bg-red-50", border: "border-red-200" },
};

const SUBTITLE_BY_SLUG = {
  "demo-ecommerce-tracker":
    "Tienda online con Meta Pixel y Google Analytics cargados antes del consentimiento del usuario.",
  "demo-clinica-datos-salud":
    "Centro de salud que almacena información médica sin base legal explícita ni cifrado documentado.",
  "demo-pyme-sin-politica":
    "Microempresa que recopila emails y RUTs sin política de privacidad publicada en el sitio.",
};

function formatRangeShort(montoMin, montoMax) {
  if (!montoMax) return "—";
  const fmt = (n) => {
    if (n >= 1_000_000_000) return `$${(n / 1_000_000_000).toFixed(1).replace(/\.0$/, "")} mil M`;
    if (n >= 1_000_000) return `$${Math.round(n / 1_000_000)} M`;
    return `$${n.toLocaleString("es-CL")}`;
  };
  return `${fmt(montoMin)} – ${fmt(montoMax)} CLP`;
}

export default function RiesgoDemo() {
  const [demos, setDemos] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancel = false;
    axios
      .get(`${API}/simulador-multas/demos`)
      .then((r) => {
        if (!cancel) setDemos(r.data?.demos || []);
      })
      .catch(() => {
        if (!cancel) setDemos([]);
      })
      .finally(() => {
        if (!cancel) setLoading(false);
      });
    return () => {
      cancel = true;
    };
  }, []);

  if (!loading && demos.length === 0) return null;

  return (
    <section
      data-testid="riesgo-demo-section"
      className="px-6 py-20 sm:py-28 bg-gradient-to-b from-slate-50 to-white border-y border-slate-200"
    >
      <div className="max-w-6xl mx-auto">
        <div className="max-w-2xl mb-12">
          <div className="inline-flex items-center gap-2 text-xs font-semibold text-blue-700 bg-blue-50 border border-blue-200 px-3 py-1.5 rounded-full mb-4">
            <Calculator className="w-3.5 h-3.5" />
            Simulador de multas
          </div>
          <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight">
            Calcula tu riesgo en 30 segundos
          </h2>
          <p className="mt-4 text-base text-slate-600 leading-relaxed">
            Estos son escenarios típicos bajo la Ley 21.719, con cálculo determinístico
            según el artículo aplicable y el rango de UTM vigente.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {(loading ? Array.from({ length: 3 }) : demos).map((demo, i) => {
            if (loading) {
              return (
                <div
                  key={i}
                  className="bg-white border border-slate-200 rounded-2xl p-6 animate-pulse h-64"
                />
              );
            }
            const cat = (demo.categoria_principal || "leve").toLowerCase();
            const meta = ICON_BY_CATEGORY[cat] || ICON_BY_CATEGORY.leve;
            const Icon = meta.icon;
            const isAmonest = demo.escenario === "amonestacion_pyme_primera_vez";

            return (
              <Link
                key={demo.slug}
                to={`/multas/${demo.slug}`}
                data-testid={`riesgo-demo-card-${demo.slug}`}
                className="group relative bg-white border border-slate-200 rounded-2xl p-6 hover:border-blue-300 hover:shadow-lg transition-all duration-200 flex flex-col"
              >
                <div className={`inline-flex items-center gap-1.5 self-start text-[11px] font-bold uppercase tracking-wide ${meta.color} ${meta.bg} ${meta.border} border px-2.5 py-1 rounded-full`}>
                  <Icon className="w-3 h-3" />
                  {isAmonest ? "Amonestación" : `Multa ${demo.categoria_label}`}
                </div>

                <h3 className="mt-4 text-lg font-bold text-slate-900 leading-snug">
                  {demo.titulo_demo}
                </h3>
                <p className="mt-2 text-sm text-slate-600 leading-relaxed flex-1">
                  {SUBTITLE_BY_SLUG[demo.slug] || ""}
                </p>

                <div className="mt-5 pt-4 border-t border-slate-100">
                  <p className="text-xs text-slate-500 uppercase tracking-wide font-medium">
                    Rango estimado
                  </p>
                  <p className="mt-1 text-base font-bold text-slate-900 tabular-nums">
                    {isAmonest ? "Amonestación escrita (sin multa)" : formatRangeShort(demo.monto_min, demo.monto_max)}
                  </p>
                </div>

                <span className="mt-5 inline-flex items-center gap-1 text-sm font-semibold text-blue-600 group-hover:text-blue-700">
                  Ver detalle
                  <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
                </span>
              </Link>
            );
          })}
        </div>

        <div className="mt-10 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 bg-slate-900 text-white rounded-2xl px-6 py-6 sm:px-8">
          <div>
            <p className="text-sm font-semibold">
              ¿Quieres calcular tu propio caso?
            </p>
            <p className="text-xs text-slate-300 mt-1">
              El simulador completo te pregunta solo 4 cosas y entrega tu rango exacto.
            </p>
          </div>
          <Link
            to="/simulador-multas"
            data-testid="riesgo-demo-cta-simulador"
            className="inline-flex items-center gap-2 bg-white text-slate-900 font-semibold text-sm px-5 py-2.5 rounded-full hover:bg-slate-100 transition-colors shrink-0"
          >
            Abrir simulador
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>

        <p className="mt-6 text-xs text-slate-500 max-w-3xl leading-relaxed">
          Estimación educativa basada en rangos legales (Art. 34 bis/ter/quáter). El monto real lo define la APDP caso a caso según proporcionalidad, intencionalidad y daño causado.
        </p>
      </div>
    </section>
  );
}
